import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import {
  InformationScreenTypeEnum,
  screenLevelErrorMessageCodes,
} from 'features/otp/information/InformationScreenTypeEnum';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useAuth } from 'shared/providers/auth';
import theme from 'shared/styles/theme';

const Information = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'resetPassword.information',
  });
  const { handleLogout, isLoggedIn } = useAuth();

  const screenType = useLocalSearchParams<{
    screenType?: InformationScreenTypeEnum;
    errorCode?: string | undefined;
    message?: string;
    title?: string;
    isPhoneChallenge?: string;
    flowKey?: string;
  }>();

  useViewDidFocusScreenAnalyticsEvent(
    screenType.isPhoneChallenge === 'true'
      ? {
          key: 'reset-password-sms-error',
          customAttributes: {
            verification_type: 'phone',
            failure_reason: screenType.message ?? '',
          },
        }
      : {
          key: 'reset-password-email-error',
        },
  );

  const onErrorScreenPrimaryButtonAction = () => {
    if (screenType.screenType === InformationScreenTypeEnum.AccountNotFound) {
      router.dismiss();
    } else {
      router.dismissAll();
      router.replace('/login');
    }
  };

  const onDeviceBackButtonAction = () => {
    if (isLoggedIn && screenType.flowKey === OtpFlowKey.CARD_CREDENTIALS) {
      router.dismissAll();
      router.replace('/myvaro/card');
    } else if (isLoggedIn && screenType.flowKey !== OtpFlowKey.REACTIVATION) {
      router.replace('/myvaro/personal-details');
    } else {
      if (screenType.screenType === InformationScreenTypeEnum.MaxLimitReached) {
        router.dismissAll();
        router.replace('/login');
      } else {
        void handleLogout({ shouldNavigateToLogin: true });
      }
    }
  };

  const getErrorHeader = (screenType) => {
    if (screenType === InformationScreenTypeEnum.AccountNotFound) {
      return translatedText('accountNotFoundScreen.header');
    } else if (screenType === InformationScreenTypeEnum.Error) {
      return translatedText('errors.header');
    }
    return translatedText('genericErrors.header');
  };

  const getErrorBody = (screenType) => {
    if (screenType === InformationScreenTypeEnum.AccountNotFound) {
      return translatedText('accountNotFoundScreen.body');
    } else if (screenType === InformationScreenTypeEnum.Error) {
      return translatedText('errors.body');
    }
    return translatedText('genericErrors.body');
  };

  useBackHandler(onDeviceBackButtonAction);

  const handlelinkExpiredAnalytics = usePressableAnalytics({
    analyticsId: 'linkExpired',
  });
  const handleVerificationFailedAnalytics = usePressableAnalytics({
    analyticsId: 'verificationFailed',
    customAttributes: {
      verification_type:
        screenType.isPhoneChallenge === 'true'
          ? 'phone_otp'
          : 'email_link_verification',
    },
  });

  useFocusEffect(
    useCallback(() => {
      if (screenType.screenType === InformationScreenTypeEnum.LinkExpired) {
        handlelinkExpiredAnalytics?.();
        handleVerificationFailedAnalytics?.();
      }
    }, [
      handleVerificationFailedAnalytics,
      handlelinkExpiredAnalytics,
      screenType.screenType,
    ]),
  );

  if (
    screenLevelErrorMessageCodes.includes(screenType.errorCode as string) ||
    screenType.screenType === InformationScreenTypeEnum.MaxLimitReached
  ) {
    return (
      <View style={styles.container}>
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
        <ErrorScreen
          screenType={ErrorScreenType.Error}
          messageHeadline={screenType.title}
          messageBody={screenType.message}
          primaryButton={{
            title: translatedText('buttons.done'),
            onPress: () => {
              onDeviceBackButtonAction();
            },
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      {screenType.screenType === InformationScreenTypeEnum.LinkExpired ? (
        <ErrorScreen
          primaryButton={{
            title: translatedText('buttons.resetPassword'),
            onPress: () => {
              router.dismissAll();
              router.replace('/login');
              router.navigate('/reset-password/find-account');
            },
            analytics: {
              analyticsId: 'resetPassword',
              customAttributes: {
                login_method: 'password',
              },
            },
          }}
          secondaryButton={{
            title: translatedText('buttons.login'),
            onPress: () => {
              void handleLogout({ shouldNavigateToLogin: true });
            },
          }}
          messageHeadline={
            screenType.title ?? translatedText('linkExpired.header')
          }
          messageBody={screenType.message ?? translatedText('linkExpired.body')}
          screenType={ErrorScreenType.Error}
        />
      ) : (
        <ErrorScreen
          primaryButton={{
            title:
              screenType.screenType ===
              InformationScreenTypeEnum.AccountNotFound
                ? translatedText('buttons.tryAgain')
                : translatedText('buttons.done'),
            onPress: () => {
              onErrorScreenPrimaryButtonAction();
            },
            analytics:
              screenType.screenType ===
              InformationScreenTypeEnum.AccountNotFound
                ? {
                    analyticsId: 'tryAgainClicked',
                    customAttributes: { cta: translatedText('tryAgain') },
                  }
                : undefined,
          }}
          messageHeadline={getErrorHeader(screenType.screenType)}
          messageBody={getErrorBody(screenType.screenType)}
          screenType={ErrorScreenType.Error}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    minWidth: `100%`,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
});

export default Information;
